// @flow

import React, { type Node, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { IfFeature, hasFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';

import { useSearchZipcodeDynamically } from '@tectonics/look-and-feel/commons/brazilian';
import { useDeviceType } from '../../../../commons/device';

import { FormRoot } from '../commons';
import { FieldBrazilianZipcode } from './field-brazilian-zipcode';
import { FieldBrazilianState } from './field-brazilian-state';
import { FieldBrazilianCity } from './field-brazilian-city';
import { FieldBrazilianAddress } from './field-brazilian-address';
import { FieldBrazilianStreetNumber } from './field-brazilian-street-number';
import { FieldBrazilianStreetComplement } from './field-brazilian-street-complement';

export function BrazilianAddressGroup(): Node {
  const allFeatures = useAllFeatures();
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'mobile';
  const [isSearching, hasBeenSearchedBefore] = useSearchZipcodeDynamically();
  const [displaying, setDisplaying] = useState<bool>(hasBeenSearchedBefore);

  useEffect(() => {
    if (hasBeenSearchedBefore) {
      setTimeout(() => {
        setDisplaying(true);
      }, 10);
    }
  }, [hasBeenSearchedBefore]);

  return (
    <>
      <IfFeature allFeatures={allFeatures} wanted="field_brazilian_zipcode">
        <FieldBrazilianZipcode isSearching={isSearching} />
      </IfFeature>

      <AddressGroupWrapper active={hasBeenSearchedBefore} visible={displaying}>
        <IfFeature allFeatures={allFeatures} wanted="field_brazilian_state">
          <FieldBrazilianState rootStyle={{ gridColumn:(isMobile? '1/3' : '1/2') }} readOnly={isSearching} />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="field_brazilian_city">
          <FieldBrazilianCity rootStyle={{ gridColumn:(isMobile? '1/3' : '2/3') }} readOnly={isSearching} />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="field_brazilian_address">
          <FieldBrazilianAddress readOnly={isSearching} />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="field_brazilian_street_number">
          <FieldBrazilianStreetNumber rootStyle={{ gridColumn:(isMobile? '1/3' : '1/2') }} readOnly={isSearching} />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="field_brazilian_street_complement">
          <FieldBrazilianStreetComplement
            readOnly={isSearching} 
            rootStyle={{
              gridColumn:(isMobile || !hasFeature(allFeatures, 'field_brazilian_street_number') ? '1/3' : '2/3'),
            }}
          />
        </IfFeature>
      </AddressGroupWrapper>
    </>
  );
}

const AddressGroupWrapper = styled(FormRoot)`
  display: none;
  overflow: hidden;
  max-height: 0;
  transition: max-height 1.0s ease-out;

  ${({ active }) => active && css`
    display: block;
    display: grid;
  `}

  ${({ visible }) => visible && css`
    max-height: 1000px;
  `}
`;
